// src/components/departments/AddStaffForm.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, doc, addDoc, updateDoc, getDocs, arrayUnion, query, where } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { useAuth } from '../../contexts/AuthContext';

function AddStaffForm({ departmentId, onStaffAdded, onCancel }) {
  const { t } = useTranslation('staff');
  const { currentUser } = useAuth();
  const [tab, setTab] = useState('new'); // 'new' or 'existing'
  
  // New staff form state
  const [staffName, setStaffName] = useState('');
  const [pin, setPin] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('staff');
  const [startDate, setStartDate] = useState(new Date());
  
  // Existing staff search state
  const [searchText, setSearchText] = useState('');
  const [existingStaff, setExistingStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load existing staff for the company that are not in this department
  useEffect(() => {
    const fetchExistingStaff = async () => {
      if (!currentUser || !departmentId) return;
      
      try {
        const companyId = currentUser.uid;
        const staffRef = collection(db, 'companies', companyId, 'staff');
        const querySnapshot = await getDocs(staffRef);
        
        const allStaff = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter out staff that are already in this department
        const availableStaff = allStaff.filter(staff => 
          !staff.departmentIds?.includes(departmentId)
        );
        
        setExistingStaff(availableStaff);
        setFilteredStaff(availableStaff);
      } catch (err) {
        console.error('Error fetching existing staff:', err);
        setError(t('errors.failedToLoadStaff'));
      }
    };
    
    if (tab === 'existing') {
      fetchExistingStaff();
    }
  }, [currentUser, departmentId, tab, t]);

  // Update filtered staff when search text changes
  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredStaff(existingStaff);
    } else {
      const filtered = existingStaff.filter(staff => 
        staff.name.toLowerCase().includes(searchText.toLowerCase()) ||
        (staff.email && staff.email.toLowerCase().includes(searchText.toLowerCase()))
      );
      setFilteredStaff(filtered);
    }
  }, [searchText, existingStaff]);

  const handleAddNewStaff = async (e) => {
    e.preventDefault();
    
    // Validate input
    if (!staffName.trim()) {
      setError(t('validation.nameRequired'));
      return;
    }
    
    if (pin.length !== 4 || !/^\d+$/.test(pin)) {
      setError(t('validation.pinRequired'));
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const companyId = currentUser.uid;
      
      // Current year for vacation days tracking
      const currentYear = new Date().getFullYear().toString();
      const vacationDaysValue = 25; // Default vacation days
      const vacationDaysObj = { [currentYear]: vacationDaysValue };
      
      // Create employment info object (contractHours/workSchedule removed -
      // Schedule Planner shifts are the source of truth for hours)
      const employmentInfo = {
        startDate,
        vacationDays: vacationDaysObj
      };
      
      // Create staff settings object
      const settings = {
        locationTrackingEnabled: false,
        notificationsEnabled: true
      };
      
      // Create the staff document
      const staffRef = collection(db, 'companies', companyId, 'staff');
      const staffDoc = await addDoc(staffRef, {
        name: staffName.trim(),
        pin,
        email: email.trim(),
        role,
        departmentIds: [departmentId],
        primaryDepartmentId: departmentId,
        deviceId: '',
        settings,
        employmentInfo
      });
      
      // Update the department's staffIds array
      const departmentRef = doc(db, 'companies', companyId, 'departments', departmentId);
      await updateDoc(departmentRef, {
        staffIds: arrayUnion(staffDoc.id)
      });
      
      if (onStaffAdded) {
        onStaffAdded();
      }
    } catch (err) {
      console.error('Error adding new staff:', err);
      setError(t('errors.failedToAddStaff'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssignExistingStaff = async (staffId) => {
    if (!currentUser || !departmentId || !staffId) return;
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const companyId = currentUser.uid;
      
      // Update the staff member's departmentIds array
      const staffRef = doc(db, 'companies', companyId, 'staff', staffId);
      await updateDoc(staffRef, {
        departmentIds: arrayUnion(departmentId)
      });
      
      // Update the department's staffIds array
      const departmentRef = doc(db, 'companies', companyId, 'departments', departmentId);
      await updateDoc(departmentRef, {
        staffIds: arrayUnion(staffId)
      });
      
      if (onStaffAdded) {
        onStaffAdded();
      }
    } catch (err) {
      console.error('Error assigning existing staff:', err);
      setError(t('errors.failedToAssignStaff'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-staff-form">
      <div className="form-header">
        <h2>{t('modal.addStaff')}</h2>
        <button onClick={onCancel} className="close-button">×</button>
      </div>
      
      <div className="tab-selector">
        <button 
          className={`tab-button ${tab === 'new' ? 'active' : ''}`}
          onClick={() => setTab('new')}
        >
          {t('modal.newStaff')}
        </button>
        <button 
          className={`tab-button ${tab === 'existing' ? 'active' : ''}`}
          onClick={() => setTab('existing')}
        >
          {t('modal.existingStaff')}
        </button>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {tab === 'new' ? (
        <form onSubmit={handleAddNewStaff} className="new-staff-form">
          <div className="form-tabs">
            <button type="button" className="form-tab active">{t('modal.basicInfo')}</button>
          </div>
          
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="staff-name">{t('form.name')}</label>
              <input
                id="staff-name"
                type="text"
                value={staffName}
                onChange={(e) => setStaffName(e.target.value)}
                placeholder={t('form.staffNamePlaceholder')}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="staff-pin">{t('form.pin')}</label>
              <input
                id="staff-pin"
                type="text"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder={t('form.pinPlaceholder')}
                maxLength={4}
                pattern="\d{4}"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="staff-email">{t('form.email')}</label>
              <input
                id="staff-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('form.emailPlaceholder')}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="staff-role">{t('form.role')}</label>
              <select
                id="staff-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="staff">{t('roles.staff')}</option>
                <option value="manager">{t('roles.manager')}</option>
                <option value="admin">{t('roles.admin')}</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="start-date">{t('form.startDate')}</label>
              <input
                id="start-date"
                type="date"
                value={startDate.toISOString().split('T')[0]}
                onChange={(e) => setStartDate(new Date(e.target.value))}
              />
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('buttons.adding') : t('buttons.addStaff')}
            </button>
            <button 
              type="button" 
              className="cancel-button"
              onClick={onCancel}
            >
              {t('buttons.cancel')}
            </button>
          </div>
        </form>
      ) : (
        <div className="existing-staff-form">
          <div className="search-container">
            <input
              type="text"
              placeholder={t('modal.searchPlaceholder')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="staff-list">
            {filteredStaff.length === 0 ? (
              <div className="empty-list">
                <p>{t('modal.noAvailableStaff')}</p>
              </div>
            ) : (
              filteredStaff.map((staff) => (
                <div key={staff.id} className="staff-list-item">
                  <div className="staff-info">
                    <div className="staff-name">{staff.name}</div>
                    <div className="staff-email">{staff.email || t('modal.noEmail')}</div>
                  </div>
                  
                  <button
                    onClick={() => handleAssignExistingStaff(staff.id)}
                    disabled={isSubmitting}
                    className="assign-button"
                  >
                    {t('buttons.assign')}
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AddStaffForm;